import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import {
  AutoCompleteExtraProps,
  FormHandles,
  FormFieldDefinitionType,
  EditableTableFieldProps,
} from "models/propTypes/genericFormProps";
import { FormEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { SettingsFormTypes } from "models/formTypes";
import { ProfileSettingsFormProps } from "models/propTypes/settingsPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { GenericForm } from "components/common";
import { AddSupportCaseLink } from "components/support";
//#endregion
import { LanguageHelper, LocalizationHelperInstance } from "utils/helpers";

const ProfileSettingsForm: ForwardRefRenderFunction<FormHandles, ProfileSettingsFormProps> = (props, ref) => {
  const { getPaymentMethodsTableExtraProps } = props;

  return (
    <GenericForm<SettingsFormTypes.ProfileSettings>
      formRef={ref}
      inline
      {...props}
      fields={[
        {
          label: TranslationKeys.Settings.ProfileSettings,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WUser,
          innerFields: {
            name: {
              label: TranslationKeys.Common.Name,
              descriptionText: TranslationKeys.Settings.ProfileNameDescription,
              type: FormEnums.FormFieldType.TEXT,
            },
            mail: {
              label: TranslationKeys.Common.Email,
              descriptionText: (
                <>
                  <AddSupportCaseLink />{" "}
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.ProfileMailDescription)}
                </>
              ),
              type: FormEnums.FormFieldType.TEXT,
              disabled: true,
            },
            phone: {
              label: TranslationKeys.Settings.CompanyPhone,
              descriptionText: TranslationKeys.Settings.ProfilePhoneDescription,
              type: FormEnums.FormFieldType.TEXT,
            },
            bankAccount: {
              label: TranslationKeys.Settings.AccountNumberForSalary,
              descriptionText: TranslationKeys.Settings.AccountNumberDescription,
              type: FormEnums.FormFieldType.TEXT,
            },
            bankAccountClearing: {
              label: TranslationKeys.Settings.ClearingNumberForSalary,
              descriptionText: TranslationKeys.Settings.ClearingNumberDescription,
              type: FormEnums.FormFieldType.TEXT,
            },
            additionalBankAccount: {
              label: TranslationKeys.Settings.AccountForExpenses,
              descriptionText: TranslationKeys.Settings.AccountNumberDescription,
              type: FormEnums.FormFieldType.TEXT,
            },
            additionalBankAccountClearing: {
              label: TranslationKeys.Settings.ClearingNumberForExpenses,
              descriptionText: TranslationKeys.Settings.ClearingNumberForExpensesDescription,
              type: FormEnums.FormFieldType.TEXT,
            },
            profileImage: {
              label: TranslationKeys.Settings.ProfilePicture,
              descriptionText: TranslationKeys.Settings.ProfilePictureDescription,
              type: FormEnums.FormFieldType.FILEUPLOADER,
              extraProps: { fileUploaderType: "full", maxFileCount: 1 },
            },
            languageName: {
              label: TranslationKeys.Common.Language,
              descriptionText: TranslationKeys.Settings.ProfileLanguageDescription,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              extraProps: {
                referenceFieldName: FormFieldConstants.Settings.ProfileSettings.LanguageKey,
                items: LanguageHelper.getLanguageAutoCompleteList(),
              } as AutoCompleteExtraProps<SettingsFormTypes.ProfileSettings>,
            },
          },
        },
        {
          label: TranslationKeys.Settings.DailyMails,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WAt,
          innerFields: {
            includeOverdueInvoices: {
              label: TranslationKeys.Settings.IncludeOverdueInvoices,
              descriptionText: TranslationKeys.Settings.IncludeOverdueInvoicesDescription,
              type: FormEnums.FormFieldType.TOGGLE,
            },
            noDailyMailOnWeekends: {
              label: TranslationKeys.Settings.EmailsOnWeekdays,
              descriptionText: TranslationKeys.Settings.EmailsOnWeekdaysDescription,
              type: FormEnums.FormFieldType.TOGGLE,
            },
            useCompanySpecificEmail: {
              label: TranslationKeys.Settings.UseCompanySpecificEmail,
              descriptionText: TranslationKeys.Settings.UseCompanySpecificEmailDescription,
              type: FormEnums.FormFieldType.TOGGLE,
            },
            companySpecificEmails: {
              label: TranslationKeys.Settings.MailAddresses,
              descriptionText: TranslationKeys.Settings.MailAddressesDescription,
              hidden: (values: SettingsFormTypes.ProfileSettings) => !values.useCompanySpecificEmail,
              type: FormEnums.FormFieldType.EDITABLETABLE,
              innerFields: {
                companyName: {
                  readonly: true,
                  label: "",
                  type: FormEnums.FormFieldType.TEXT,
                },
                email: {
                  label: "",
                  required: true,
                  type: FormEnums.FormFieldType.TEXT,
                },
              } as FormFieldDefinitionType<WintSuperkollAPIModel.PersonCompanyEmail>,
              extraProps: {
                hideActions: true,
                showColumnHeaders: false,
                isCompactTable: true,
              } as EditableTableFieldProps<WintSuperkollAPIModel.PersonCompanyEmail>,
            },
          },
        },
        {
          label: TranslationKeys.Settings.PayReceipts,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WReceipt,
          innerFields: {
            wintCardPaymentMethods: {
              label: TranslationKeys.Receipt.PaymentMethodType_5,
              type: FormEnums.FormFieldType.TABLELIST,
              extraProps: getPaymentMethodsTableExtraProps(true),
              hidden: (values: SettingsFormTypes.ProfileSettings) => !values.wintCardPaymentMethods?.length,
            },
            bankCardPaymentMethods: {
              label: TranslationKeys.Receipt.PaymentMethodType_0,
              type: FormEnums.FormFieldType.TABLELIST,
              extraProps: getPaymentMethodsTableExtraProps(true),
              hidden: (values: SettingsFormTypes.ProfileSettings) => !values.bankCardPaymentMethods?.length,
            },
            creditCardPaymentMethods: {
              label: TranslationKeys.Receipt.PaymentMethodType_1,
              type: FormEnums.FormFieldType.TABLELIST,
              extraProps: getPaymentMethodsTableExtraProps(true),
              hidden: (values: SettingsFormTypes.ProfileSettings) => !values.creditCardPaymentMethods?.length,
            },
            companyCashPaymentMethods: {
              label: TranslationKeys.Receipt.PaymentMethodType_3,
              type: FormEnums.FormFieldType.TABLELIST,
              extraProps: getPaymentMethodsTableExtraProps(true),
              hidden: (values: SettingsFormTypes.ProfileSettings) => !values.companyCashPaymentMethods?.length,
            },
            settlementPaymentMethods: {
              label: TranslationKeys.Receipt.PaymentMethodType_4,
              type: FormEnums.FormFieldType.TABLELIST,
              extraProps: getPaymentMethodsTableExtraProps(true),
              hidden: (values: SettingsFormTypes.ProfileSettings) => !values.settlementPaymentMethods?.length,
            },
            ownExpensePaymentMethods: {
              label: TranslationKeys.Settings.OwnExpenses,
              type: FormEnums.FormFieldType.TABLELIST,
              extraProps: getPaymentMethodsTableExtraProps(false),
            },
          },
        },
      ]}
    />
  );
};

export default forwardRef(ProfileSettingsForm);
